//
// database.ts
//
// Copyright 2024-Present Couchbase, Inc.
//
// Use of this software is governed by the Business Source License included
// in the file licenses/BSL-Couchbase.txt.  As of the Change Date specified
// in that file, in accordance with the Business Source License, use of this
// software will be governed by the Apache License, Version 2.0, included in
// the file licenses/APL2.txt.
//

import { Query } from "@/query/query";
import { assert, assertEqual } from "@/util/assert";
import { BlobStore } from "./blobStore";
import { Collection, DefaultCollectionName, type CollectionConfig } from "./collection";
import { CryptoCodec, EncryptionError } from "./cryptoCodec";
import type { CBLDictLike } from "./document";
import { BlobTableName, CheckpointStore, MetaStore, type DatabaseMeta } from "./internals";
import type { CBLDictionary } from "./types";
import type * as logtape from "@logtape/logtape";
import * as dexie from "dexie";
import { DBLogger } from "../util/logging";
import {Invalidated} from "@/util/invalidated.ts";
import {ListenerToken} from "@/util/listener.ts";

export { EncryptionError } from "./cryptoCodec";

export const ReadOnly = "r";
export const ReadWrite = "rw";

export type TransactionMode = typeof ReadOnly | typeof ReadWrite;

export type TransactionCallback<T> = () => Promise<T>;


/** Default database schema: collection names are unspecified, and document types are just
 *  {@link CBLDictionary}, i.e. any property name is allowed and values can be any JSON types. */
export type NoSchema = Record<string,CBLDictionary>;
export type SchemaLike<S> = { [K in keyof S]: CBLDictLike<S[K]> }   /** @internal */
export type SchemaKeys<S> = Extract<keyof S, string>;   /** @internal */


/** Configuration for opening a Database.
 *  @template Schema  The database schema: an interface type that maps collection names to the
 *                    interfaces that represent their documents. Defaults to none. */
export interface DatabaseConfig<Schema extends SchemaLike<Schema> = NoSchema> {
    /** Name of the database, in the browser's local IndexedDB storage. */
    readonly name: string;

    /** The version number of this database configuration.
     *  This must be incremented whenever any collection configurations change. */
    readonly version: number;

    /** The collections in the database.
     *  If Schema is given, there must be an entry for each collection name declared in the schema. */
    readonly collections: CollectionsConfig<Schema>;

    /** The password to decrypt the database with, if it's encrypted.
     *  For security reasons this property is cleared when the database is opened. */
    password?: string;
}


export type CollectionsConfig<Schema extends SchemaLike<Schema> = NoSchema> =
    {readonly [K in SchemaKeys<Schema>]: CollectionConfig<Schema[K]>};


/** Describes which properties should remain unencrypted in each collection.
 *  Key is collection name, value is array or set of property names. */
export type UnencryptedProperties = Record<string, Set<string> | Array<string>>;


/** A callback function invoked when a Database closes.
 *  @param reason  The reason for the close.
 *                  - `'close'` means a normal call to {@link close}.
 *                  - `'delete'` means the database is being deleted, either locally or in another
 *                    context (browser tab), or if the user manually clears the site's local storage.
 *                  - `'upgrade'` means another context is opening the database with a newer
 *                    schema version, which requires this connection to close.
 *                    This implies that the current running code is out of date;
 *                    you should probably call `window.location.reload()`.
 *  @param version  The new schema version, if the reason is `'upgrade'`. */
export type DatabaseCloseCallback = (reason: 'close' | 'delete' | 'upgrade', newVersion?: number) => void;


/** Types of database maintenance; passed to {@link Database.performMaintenance}. */
export type MaintenanceType = 'compact';


type DexieSchema = {[tableName: string]: string | null};
let sDexieOptions: {indexedDB: IDBFactory, IDBKeyRange: typeof IDBKeyRange} | undefined;


/** A Couchbase Lite database. Contains one or more named {@link Collection collections},
 *  which contain {@link CBLDocument documents}.
 *
 *  Call {@link open} to create a Database instance (the constructor is private.)
 *
 *  @template Schema  An optional interface type that improves type-safety of collection and
 *      document accessors. Its keys must be the names of the collections, and each value type
 *      is an interface that describes the properties of a document in that collection:
 *
 *      interface MySchema {
 *        shoes: {style: string, foot: "left" | "right", size: number},
 *      }
 *
 *  If you don't give a schema, the `collections` property will accept any string as a key,
 *  and collections will use {@link CBLDictionary} as their document type.
 *      */
export class Database<Schema extends SchemaLike<Schema> = NoSchema> {

    /** Call this to use a non-default implementation of IndexedDB.
     *  This is required in non-browser environments like Node, Bun and Deno!
     *  It should be called only once, **before creating any Database objects**.
     *  Example:
     *  ```
     *      import { indexedDB, IDBKeyRange } from "fake-indexeddb";
     *      Database.useIndexedDB(indexedDB, IDBKeyRange);
     *  ```
     */
    static useIndexedDB(indexedDB: IDBFactory, idbKeyRange: typeof IDBKeyRange) {
        sDexieOptions = {indexedDB: indexedDB, IDBKeyRange: idbKeyRange};
    }

    /** Enable's Dexie's debug mode, which provides meaningful stack backtraces in exceptions. */
    static debugMode(debug: boolean) {
        dexie.Dexie.debug = debug;
    }


    /** Creates a Database instance and opens the database. If a local IndexedDB database with
     *  this name exists, it will be opened; otherwise a new one is created.
     *  @param config  The database {@link DatabaseConfig configuration}
     *  @template Schema  An optional interface type that improves type-safety of collection and
     *      document accessors. Its keys must be the names of the collections, and each value type
     *      is an interface that describes the properties of a document in that collection. */
    static async open<Schema extends SchemaLike<Schema> = NoSchema>(
        config: DatabaseConfig<Schema>): Promise<Database<Schema>>
    {
        const password = config.password;
        config.password = undefined;    // Don't expose password in public Database.config property
        const db = new Database<Schema>(config, password);
        await db.reopen(password);
        return db;
    }


    /** @internal */
    static get idbFactory() {return sDexieOptions?.indexedDB ?? indexedDB;}
    /** @internal */
    static get idbKeyRange() {return sDexieOptions?.IDBKeyRange ?? IDBKeyRange;}


    private constructor(readonly config: DatabaseConfig<Schema>, password?: string) {
        // https://dexie.org/docs/Tutorial/Understanding-the-basics
        // https://dexie.org/docs/Tutorial/Design#database-versioning
        // https://dexie.org/docs/Version/Version.stores()

        this.name = config.name;
        this.logger = DBLogger.with({db: this.name});

        const erasedCollCfg = config.collections as unknown as Record<string,CollectionConfig>;

        const schema: DexieSchema = {
            [MetaStore]: "",
            [CheckpointStore]: "",
            [BlobTableName]: "digest",
        };
        for (const [collName, cfg] of Object.entries(erasedCollCfg)) {
            Collection.validateName(collName);
            schema[collName] = Collection.dexieIndexSpec(cfg);
        }
        if (Object.keys(erasedCollCfg).length === 0) {
            schema[DefaultCollectionName] = Collection.dexieIndexSpec({});
        }

        this.#collectionConfig = new Map(Object.entries(erasedCollCfg));
        if (this.#collectionConfig.size === 0)
            this.#collectionConfig.set(DefaultCollectionName, {});

        this.#db = new dexie.Dexie(config.name, sDexieOptions);
        this.installDBCore();    // must happen before db.version(...).stores(...)
        this.#db.version(config.version).stores(schema);

        this.#db.on("populate", async _txn => {
            // This will be called while opening a newly-created database.
            if (password !== undefined) {
                this.#codec = await Database.waitFor(CryptoCodec.create(password));
                await this.setMeta({ challenge: this.#codec.challenge });
                this.logger.info("Created new encrypted database {db}");
            } else {
                this.logger.info("Created new database {db}");
            }
        });

        this.#db.on('versionchange', (event) => {
            if (event.newVersion) {
                this.logger.warn`Another connection wants to upgrade db ${this.name} to version ${event.newVersion}. Closing now.`;
                this.#closing('upgrade', event.newVersion);
            } else {
                this.logger.warn`Another connection wants to delete db ${this.name}. Closing now.`;
                this.#closing('delete');
            }
            this.#db.close({disableAutoOpen: true});
            this.#db = Invalidated.of(this.#db, "Database has been deleted or upgraded");
            return false;
        });

        this.#db.on('close', () => {
            this.#closing('close');
        });
    }


    private async initialize(password?: string): Promise<this> {
        try {
            if (!this.#codec) {
                const metadata = await this.getMeta();
                if (metadata.challenge) {
                    const codec = CryptoCodec.withChallenge(metadata.challenge);
                    if (password === undefined || ! await codec.unlock(password))
                        throw new EncryptionError("Incorrect or missing database password");
                    this.#codec = codec;
                } else if (password !== undefined) {
                    throw new EncryptionError("Incorrect or missing database password");
                }
            }

            const anyThis = this as unknown as Database;
            let collections: Record<string,Collection> = {};
            for (const [collName, config] of this.#collectionConfig) {
                const collection = new Collection(anyThis, collName, config, this.#db, this.#codec) as unknown as Collection;
                await collection.open();
                collections[collName] = collection;
            }
            Object.freeze(collections);
            this.#collections = collections;
            return this;
        } catch (x) {
            this.close();
            throw x;
        }
    }


    /** The database's name. */
    readonly name: string;


    /** A TypeScript convenience that returns the same instance with its schema erased,
     *  i.e. as a plain `Database`. */
    get withoutSchema(): Database {return this as unknown as Database;}


    /* {@link https://logtape.org LogTape} logger instance for this Database. */
    readonly logger : logtape.Logger;


    /** True if the database is open. */
    get isOpen(): boolean {return !(this.#db instanceof Invalidated) && this.#db.isOpen();}


    /** Reopens the database after a {@link close} or {@link closeAndDelete} call.
     *  @param password  If the database is encrypted, you must provide the password. */
    async reopen(password?: string) {
        if (this.#db instanceof Invalidated)
            this.#db = Invalidated.originalOf(this.#db)!;
        this.logger.info("Opening database {db}");
        await this.#db.open();
        await this.initialize(password);
    }


    /** Closes the database.
     *
     *  You MUST NOT call any instance methods after this except for {@link reopen}.
     *
     *  You MUST NOT use or keep references to the Database's Collections; they're invalidated. */
    close() {
        this.logger.info("Closing database {db}");
        this.#closing('close');
        if (this.isOpen) {
            this.#db.close();
            this.#db = Invalidated.of(this.#db, "Database is closed");
        }
    }

    #closing(reason: 'close' | 'delete' | 'upgrade', version?: number) {
        if (Object.keys(this.#collections).length > 0) {
            for (const cb of this.#closeListeners)
                cb(reason, version);
            for (const coll of Object.values(this.#collections))
                coll.closing();
            this.#collections = {};
            this.#codec = undefined;
        }
    }


    /** Allows Database to be used with the `using` statement:
     *  ```
     *  {
     *      using db = new Database(config);
     *      // `db` will be closed implicitly when it exits scope
     *  }
     *  ```
     *  See: https://www.totaltypescript.com/typescript-5-2-new-keyword-using
     */
    [Symbol.dispose]() {this.close();}


    /** Closes the database, then deletes its persistent storage.
     *
     *  You MUST NOT call any instance methods after this,
     *  except for {@link reopen} which will recreate an empty db with the same config.
     *
     *  You MUST NOT use or keep references to the Database's Collections; they're invalidated. */
    async closeAndDelete() {
        this.logger.info("Closing and deleting database {db}");
        this.#closing('delete');
        if (this.isOpen) {
            await this.#db.delete();
            this.#db = Invalidated.of(this.#db, "Database has been deleted");
        }
    }


    /** Static method that deletes a database by name.
     *  You MUST close any open Database instance using it first. */
    static async delete(name: string) {
        DBLogger.info("Deleting database {db}", {db: name});
        // Can't use the static `dexie.Dexie.delete()` method because it internally creates a
        // new Dexie instance and calls `delete` on it ... but that instance isn't customized
        // with our `sDexieOptions` so it tries to call the built-in indexedDB and will fail
        // in a non-browser environment.
        await new dexie.Dexie(name, sDexieOptions).delete();
    }


    /** Registers a function that will be called when the Database closes.
     *  See {@link DatabaseCloseCallback} for details. */
    addCloseListener(callback: DatabaseCloseCallback): ListenerToken {
        this.#closeListeners.add(callback);
        return new ListenerToken( () => {
            this.#closeListeners.delete(callback);
        });
    }


    /** Returns true if a database with this name exists.
     *
     *  Note: Race conditions are possible: databases are shared by all open browser tabs and
     *  Workers with the same origin, so the database could be created or deleted at any instant
     *  after this call returns -- on a different thread! -- invalidating the result. */
    static async exists(name: string): Promise<boolean> {
        // Can't use the static Dexie.exists() method -- it blows up in a non-browser environment
        // since there's no indexedDB. (Same as delete(), above, but that workaround won't work.)
        const dbs = await Database.idbFactory.databases();
        return dbs.find( db => db.name === name ) !== undefined;
    }


    /** @internal (no need to document this!) */
    toString(): string {return `Database[${this.name}]`;}


    //-------- COLLECTIONS:


    /** An object whose keys are collection names and values are {@link Collection}s.
     *  In TypeScript, this object is typed accoring to the database's schema type,
     *  so accessing a nonexistent collection will result in a compile error,
     *  and Collection instances access from this will be typed according to their
     *  document schema. */
    get collections(): {readonly [K in SchemaKeys<Schema>]: Collection<Schema[K]>} {
        return this.#collections as typeof this.collections;
    }


    /** Returns the Collection object for the named collection.
     *  @throws if there is no such collection. */
    getCollection(name: string): Collection {
        const coll = this.#collections[name];
        if (coll === undefined)
            throw Error(`Database ${this.name} has no collection named '${name}'`);
        return coll;
    }


    /** The names of the Collections in this Database. */
    get collectionNames(): readonly SchemaKeys<Schema>[] {
        return Object.keys(this.collections) as SchemaKeys<Schema>[];
    }


    /** Returns the Collection named "_default".
     *  @throws if there is no such collection. */
    get defaultCollection() : Collection {
        return this.getCollection(DefaultCollectionName);
    }


    //-------- CHANGE LISTENER:


    /** Collections call this to enable/disable receiving Dexie db events. @internal */
    observeChangesFor(collection: string, enable: boolean = true) {
        if (enable) {
            // if (this.#collectionsObservingChanges.size === 0)
            //     this.startChangeListener();
            this.#collectionsObservingChanges.add(collection);
        } else {
            // (I don't know a way to remove the observer when the last collection is removed)
            this.#collectionsObservingChanges.delete(collection);
            if (this.#collectionsObservingChanges.size === 0)
                this.logger.info `Stopping Dexie change listener`;
        }
    }

    private installDBCore() {
        // https://dexie.org/docs/Dexie/Dexie.use()#example
        // https://github.com/dexie/Dexie.js/blob/master/src/public/types/dbcore.d.ts
        this.#db.use({
            stack: "dbcore",
            name: "CouchbaseLite",
            create: (realDBCore): dexie.DBCore => {
                this.logger.trace `Creating DBCore!`;
                const myTables = new Map<string,dexie.DBCoreTable>();
                return {                                    // Return new implementation of DBCore
                    ...realDBCore,                          // Copy default implementation
                    table: (tableName) => {                 // Override table method
                        let realTable = realDBCore.table(tableName);  // Construct real table
                        if (!this.#collectionConfig.has(tableName))
                            return realTable;
                        let myTable = myTables.get(tableName);
                        if (myTable)
                            return myTable;
                        this.logger.trace `Installing mutate hook for ${tableName}`;
                        myTable = {                         // Wrap Collection's table
                            ...realTable,                   // Copy default table implementation
                            mutate: async req => {          // Override the mutate method
                                return realTable.mutate(req).then(res => {
                                    this.#collections[tableName]?.onMutate(req, res);
                                    return res;
                                });
                            }
                        };
                        myTables.set(tableName, myTable);
                        return myTable;
                    }
                };
            }
        });
    }

    #collectionsObservingChanges = new Set<string>();


    //-------- QUERIES:


    /** Creates a {@link Query} object from a N1QL/SQL++ `SELECT` statement. */
    createQuery(select: string): Query<Schema> {
        return new Query(this, select);
    }


    //-------- TRANSACTIONS:


    #txnCounter = 0;

    /** Opens a transaction on one or more collections and invokes the callback.
     *  When the callback returns, the transaction commits.
     *  If the callback throws an exception, a read-write transaction is aborted.
     *
     *  **Warning:** Your callback, though declared as `async`, may not perform any async operations
     *  other than those that access the database. Trying to do network I/O or talking to Workers
     *  will trigger the dreaded "Transaction committed too soon" exception.
     *  Why? IndexedDB is very strict about not letting transactions remain open too long.
     *  If a transaction is still open when the current event loop cycle ends, it will automatically
     *  commit. The only exceptions are async operations on the database itself, for obvious
     *  reasons.
     *
     *  @param mode  'ReadWrite' or 'ReadOnly'.
     *  @param collections  Array of collections (or their names) that will be modified in this
     *              transaction.
     *  @param callback  The function that will run during the transaction.
     *  @returns  The result returned by the callback.
     *  @internal */
    async inTransaction<T>(mode: TransactionMode,
                           // eslint-disable-next-line @typescript-eslint/no-explicit-any
                           collections: ReadonlyArray<Collection<any>|string>,
                           callback: TransactionCallback<T>) : Promise<T> {
        const tables = collections.map(coll => (typeof coll === "string") ? coll : coll.name);
        const colls = collections.map(coll => (typeof coll === "string") ? this.getCollection(coll) : coll);
        if (mode === ReadWrite)
            tables.push(MetaStore, BlobTableName);

        let outermost = true;
        let indent = "";
        let id = -1;
        let committing = false;
        try {
            return await this.#db.transaction(mode, tables, async (txn) => {
                let depth = 0;
                for (let t = txn.parent; t; t = t.parent)
                    ++depth;
                outermost = (depth === 0);
                indent = "    ".repeat(depth);

                if (outermost) {
                    id = ++this.#txnCounter;
                    this.logger.debug(`>>> Begin ${mode} transaction #{id}`, {id});
                } else {
                    id = this.#txnCounter;
                    this.logger.debug(`${indent}>>> Begin nested ${mode} transaction inside #{id}`, {id});
                }

                try {
                    // Finally invoke the callback!
                    const result = await callback();

                    committing = true;
                    if (outermost && mode !== ReadOnly)
                        this.logger.debug(`... Committing transaction #{id} ...`, {id});
                    return result;
                } catch (x) {
                    this.logger.debug(`Aborting transaction #{id} due to exception`, {id});
                    throw x;
                } finally {
                    if (outermost) {
                        // Notify collections that outer transaction is ending:
                        for (let coll of colls)
                            await coll.transactionEnding(mode, committing);
                    }
                }
            });
        } catch (x) {
            if (committing)
                this.logger.error(`Exception committing transaction #{id}: ${x}`, {id});
            committing = false;
            throw x;
        } finally {
            if (outermost) {
                // Notify collections that outer transaction has committed:
                if (committing)
                    this.logger.debug(`<<< ${mode === ReadOnly ? "Ended" : "Committed"} transaction #{id}`, {id});
                for (let coll of colls)
                    coll.transactionEnded(mode, committing);
            } else {
                this.logger.debug(`${indent}<<< Ended nested transaction inside #{id}`, {id});
            }
        }
    }


    /** A more performant wrapper around `Dexie.waitFor`. This must be wrapped around promises
     *  being awaited, if you are possibly in a transaction, to prevent the dreaded "Transaction
     * committed early" exception. (Blame IndexedDB's awkward API design.) @internal */
    static async waitFor<T>(promise: Promise<T>): Promise<T> {
        if (dexie.Dexie.currentTransaction !== undefined)
            return dexie.Dexie.waitFor(promise);
        else
            return promise; // no overhead if not in a txn
    }


    //-------- ENCRYPTION:


    // (Mis)using the collection metadata table to store DB metadata, under the key ""
    private get metaTable(): dexie.Table<DatabaseMeta,string>  {return this.#db.table(MetaStore);}

    private async getMeta(): Promise<DatabaseMeta> {
        return (await this.metaTable.get("")) ?? {};
    }

    private async setMeta(meta: DatabaseMeta): Promise<void> {
        await this.metaTable.put(meta, "");
    }


    // Implementation note: Avoid doing crypto operations during a transaction
    // because they're async and will cause the transaction to commit too early.
    // In places where that's unavoidable (while saving or loading a document)
    // we have to use Dexie's `waitFor()` hack.


    /** Returns 'none' if this database is not encrypted, 'locked' if it's encrypted but locked,
     *  or 'unlocked' if it's been unlocked.
     *
     *  If the status is 'locked' you must call {@link unlock} with the correct password,
     *  otherwise loading or querying documents will throw exceptions. @internal */
    get encryptionStatus(): 'none' | 'locked' | 'unlocked' {
        assert(!dexie.Dexie.currentTransaction?.active, "Don't call this in a transaction");
        if (this.#codec)
            return this.#codec.isUnlocked ? 'unlocked' : 'locked';
        else
            return 'none';
    }


    /** Unlocks an encrypted database using the given password. @internal
     *  @returns  True on success, false if the password is wrong (or there is no encryption.) */
    async unlock(password: string): Promise<boolean> {
        assert(!dexie.Dexie.currentTransaction?.active, "Don't call this in a transaction");
        if (!this.#codec)
            return false;
        return await this.#codec.unlock(password);
    }


    /** Locks an encrypted database, making encrypted stored data inaccessible until {@link unlock}
     *  is called. Has no effect if the database is not encrypted. @internal */
    lock(): void {
        this.#codec?.lock();
    }


    /** Encrypts or decrypts a database, or changes the encryption key.
     *  @param password  The new password for encryption, or `undefined` to decrypt.
     *  @param exceptProperties  Optional top-level properties to leave unencrypted, by collection
     *                           (in addition to indexed properties, which cannot be encrypted.)
     *                           Key is collection name, value is array or set of properties.
     *  @throws EncryptionError  if it's already encrypted. */
    async changeEncryptionKey(password: string | undefined,
                              exceptProperties?: UnencryptedProperties) {
        assert(!dexie.Dexie.currentTransaction?.active, "Don't call this in a transaction");
        assert(this.encryptionStatus !== 'locked', "Database must be unlocked to change encryption");

        const newCodec = password !== undefined ? await CryptoCodec.create(password) : undefined;

        // Remember each collection's unencrypted properties in case we have to back out:
        const oldExceptProperties = new Map<string, Set<string> | undefined>();
        for (const [name, collection] of Object.entries(this.#collections))
            oldExceptProperties.set(name, collection.unencryptedProperties);

        const what = newCodec ? (this.#codec ? "Rekey" : "Encrypt") : "Decrypt";
        this.logger.info `${what}ing database...`;
        try {
            await this.inTransaction(ReadWrite, this.collectionNames, async () => {
                // Add/remove "challenge" to db metadata:
                const metadata = await this.getMeta();
                metadata.challenge = newCodec?.challenge;
                await this.setMeta(metadata);

                // Encrypt blobs:
                await this.blobStore.rekey(newCodec);

                // Encrypt all collections:
                for (const [name, collection] of Object.entries(this.#collections))
                    await collection.rekey(newCodec, exceptProperties?.[name]);
            });
            this.#codec = newCodec;
            this.logger.info `...${what}ed database!`;

        } catch (x) {
            this.logger.error `${what}ing database failed! ${x}`;
            this.blobStore.resetEncryption(this.#codec);
            for (const [name, collection] of Object.entries(this.#collections))
                collection.resetEncryption(this.#codec, oldExceptProperties.get(name));
            throw x;
        }
    }


    /** Decrypts the database. (Same as `encrypt(undefined)`.) */
    async decrypt() {await this.changeEncryptionKey(undefined);}


    //-------- INTERNALS:


    /** Adds a document, returning `undefined` on success, else `ConstraintError`. @internal */
    async tryAdd<T,TKey>(table: dexie.Table<T,TKey,T>, doc: T, key?: TKey) : Promise<Error | undefined> {
        return await table.add(doc, key)
            .then( (_key) => {return undefined;})
            .catch( async (x) => {
                const err = x as Error;
                if (err instanceof dexie.Dexie.ConstraintError)
                    return err;
                else
                    return Promise.reject(err);
            } );
    }


    /** @internal */
    get blobStore(): BlobStore<Schema> {
        if (!this.#blobStore)
            this.#blobStore = new BlobStore(this, this.#db.table(BlobTableName), this.#codec);
        return this.#blobStore;
    }


    /** Returns the number of blobs stored in the database. */
    async countBlobs(): Promise<number> {
        return this.blobStore.countBlobs();
    }


    /** Deletes all blobs that are no longer referenced by any documents.
     *  @returns  The number of blobs deleted. */
    async performMaintenance(type: MaintenanceType) {
        assertEqual(type, 'compact');
        this.logger.info("Garbage-collecting blobs");
        return await this.inTransaction(ReadWrite, this.collectionNames, async () => {
            const allDigests = new Set<string>;
            for (const collection of Object.values(this.#collections))
                await collection.collectBlobDigests(allDigests);
            return await this.blobStore.deleteBlobsExcept(allDigests);
        });
    }


    /** Used as a callback in Blob objects. @internal */
    blobLoader = async (digest: string, _type?: string) => {
        return await this.blobStore.getBlob(digest);
    };


    /** @internal  Exposed for testing */
    enableAutoExpiry   = true;

    #db                 : dexie.Dexie;
    #collectionConfig   = new Map<string,CollectionConfig>();
    #collections        : Record<string,Collection> = {};
    #blobStore?         : BlobStore<Schema>;
    #codec?             : CryptoCodec;
    #closeListeners     = new Set<DatabaseCloseCallback>;
}
